import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Play, ArrowRight, ExternalLink, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SARAH_IMAGE = "https://customer-assets.emergentagent.com/job_921fdaa6-c268-4802-9506-ec098e2d8f5d/artifacts/2vnwohj5_A4B581D1-C3D3-471E-855F-D1C62FEFE877.PNG";
const SPOTIFY_URL = "https://open.spotify.com/artist/4mVbIGHmeO5AcP5TrLk2V2";

export default function Home() {
  const { t } = useLanguage();
  const [featuredAlbums, setFeaturedAlbums] = useState([]);
  const [newRelease, setNewRelease] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [featuredRes, newReleaseRes] = await Promise.all([
          axios.get(`${API}/albums/featured`),
          axios.get(`${API}/albums/new-release`)
        ]);
        setFeaturedAlbums(featuredRes.data);
        if (newReleaseRes.data) {
          setNewRelease(newReleaseRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const videos = [
    { id: '5WVDgv02sNQ', title: 'Derrama La Lluvia', views: '12K' },
    { id: '_qWmyzVo5MU', title: 'Aunque Sea Difícil ft. Michelle Matius', views: '162K' },
    { id: 'Pz_D6HZ4vY4', title: 'Mil Gracias (Video Oficial)', views: '40K' },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero - Full bleed with text overlay */}
      <section className="relative h-screen flex items-center overflow-hidden bg-charcoal" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={SARAH_IMAGE}
            alt="Sarah Capeles"
            className="w-full h-full object-cover object-[65%_25%] brightness-[0.6]"
            data-testid="hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/80 via-charcoal/40 via-50% to-transparent" />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-xl">
            <h1 className="font-playfair text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-[1.05] drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
              Sarah<br />Capeles
            </h1>
            <p className="font-playfair italic text-gold-400 text-2xl sm:text-3xl mb-6 drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]" data-testid="hero-tagline">
              {t('Christian Singer', 'Cantante Cristiana')}
            </p>
            <div className="w-12 h-px bg-gold-500 mb-6" />
            <p className="font-lato text-base text-white/80 max-w-sm mb-10 leading-relaxed">
              {t(
                'Inspirational music that lifts you to the heavenly throne.',
                'Música inspiradora que te eleva al trono celestial.'
              )}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/music" data-testid="cta-listen-now">
                <Button className="bg-gold-500 hover:bg-gold-400 text-white font-lato text-sm uppercase tracking-wider px-8 py-5 rounded-none">
                  <Play className="w-4 h-4 mr-2" />
                  {t('Listen Now', 'Escuchar Ahora')}
                </Button>
              </Link>
              <a
                href={SPOTIFY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-spotify"
              >
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 font-lato text-sm uppercase tracking-wider px-8 py-5 rounded-none">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Spotify
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <div className="w-5 h-9 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2.5 bg-gold-400 rounded-full" />
          </div>
        </div>
      </section>

      {/* New Release Banner */}
      {newRelease && (
        <section className="bg-charcoal border-b border-white/5" data-testid="new-release-section">
          <div className="container mx-auto px-4 md:px-8">
            <div className="flex flex-col sm:flex-row items-center gap-6 py-6">
              <div className="flex items-center gap-2.5 shrink-0">
                <Sparkles className="w-4 h-4 text-gold-400" />
                <span className="font-lato text-gold-400 text-xs uppercase tracking-[0.3em] font-semibold">
                  {t('New Release', 'Nuevo Lanzamiento')}
                </span>
              </div>
              <Link to={`/music/${newRelease.id}`} className="group flex items-center gap-4 flex-1">
                <img
                  src={newRelease.image_url}
                  alt={t(newRelease.title, newRelease.title_es)}
                  className="w-12 h-12 rounded object-cover shadow-lg group-hover:scale-105 transition-transform duration-300"
                  data-testid="new-release-image"
                />
                <div>
                  <h3 className="font-playfair text-base font-semibold text-white group-hover:text-gold-400 transition-colors" data-testid="new-release-title">
                    {t(newRelease.title, newRelease.title_es)}
                  </h3>
                  <p className="font-lato text-gray-500 text-xs">{newRelease.year}</p>
                </div>
              </Link>
              <Link to={`/music/${newRelease.id}`} data-testid="new-release-cta">
                <Button className="bg-gold-500 hover:bg-gold-400 text-white font-lato text-xs uppercase tracking-wider px-5 py-2.5 rounded-full">
                  {t('Listen Now', 'Escuchar Ahora')}
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Scripture - Minimal divider */}
      <section className="py-20 bg-white" data-testid="verse-section">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <div className="max-w-2xl mx-auto">
            <div className="w-10 h-px bg-gold-400 mx-auto mb-8" />
            <blockquote className="font-playfair text-xl md:text-2xl text-charcoal/80 italic leading-relaxed mb-5">
              {t(
                '"Sing to the Lord a new song; sing to the Lord, all the earth."',
                '"Cantad al Señor un cántico nuevo; cantad al Señor, toda la tierra."'
              )}
            </blockquote>
            <p className="font-lato text-gold-500 text-xs uppercase tracking-[0.3em]">
              — {t('Psalm 96:1', 'Salmo 96:1')}
            </p>
          </div>
        </div>
      </section>

      {/* Featured Music - Clean grid */}
      <section className="py-20 bg-cream/50" data-testid="featured-albums-section">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-14">
            <div>
              <p className="font-lato text-gold-500 text-xs uppercase tracking-[0.3em] mb-2">
                {t('Featured', 'Destacado')}
              </p>
              <h2 className="font-playfair text-3xl md:text-4xl font-bold text-charcoal">
                {t('Music', 'Música')}
              </h2>
            </div>
            <Link to="/music" className="hidden md:flex items-center gap-2 font-lato text-sm text-gold-600 hover:text-gold-700 transition-colors uppercase tracking-wider">
              {t('View All', 'Ver Todos')}
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-gray-100 animate-pulse aspect-square rounded" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {featuredAlbums.slice(0, 3).map((album) => (
                <Link
                  key={album.id}
                  to={`/music/${album.id}`}
                  className="group"
                  data-testid={`album-card-${album.id}`}
                >
                  <div className="relative overflow-hidden bg-gray-50 aspect-square mb-5">
                    <img
                      src={album.image_url}
                      alt={album.title}
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
                    />
                  </div>
                  <h3 className="font-playfair text-lg font-semibold text-charcoal group-hover:text-gold-600 transition-colors">
                    {t(album.title, album.title_es)}
                  </h3>
                  <div className="flex items-center gap-3 mt-1.5">
                    <p className="font-lato text-gray-400 text-sm">{album.year}</p>
                    <span className="w-1 h-1 bg-gray-300 rounded-full" />
                    <p className="font-lato text-gold-600 text-sm font-medium">${album.price.toFixed(2)}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="mt-10 text-center md:hidden">
            <Link to="/music">
              <Button variant="outline" className="border-gold-400 text-gold-600 font-lato text-sm uppercase tracking-wider">
                {t('View All Music', 'Ver Toda la Música')}
                <ArrowRight className="w-3.5 h-3.5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Latest Videos - Minimal dark section */}
      <section className="py-20 bg-charcoal" data-testid="videos-section">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-14">
            <div>
              <p className="font-lato text-gold-400 text-xs uppercase tracking-[0.3em] mb-2">
                {t('Watch & Listen', 'Ver y Escuchar')}
              </p>
              <h2 className="font-playfair text-3xl md:text-4xl font-bold text-white">
                {t('Videos', 'Videos')}
              </h2>
            </div>
            <a
              href="https://www.youtube.com/sfrodelly"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-2 font-lato text-sm text-gold-400 hover:text-gold-300 transition-colors uppercase tracking-wider"
            >
              {t('View All', 'Ver Todos')}
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {videos.map((video) => (
              <a
                key={video.id}
                href={`https://www.youtube.com/watch?v=${video.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group"
                data-testid={`video-card-${video.id}`}
              >
                <div className="relative aspect-video bg-gray-900 overflow-hidden mb-4">
                  <img
                    src={`https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out opacity-80 group-hover:opacity-100"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 bg-gold-500/90 rounded-full flex items-center justify-center group-hover:bg-gold-400 transition-colors transform group-hover:scale-110 duration-300">
                      <Play className="w-6 h-6 text-white ml-0.5" />
                    </div>
                  </div>
                </div>
                <h3 className="font-playfair text-base font-semibold text-white group-hover:text-gold-400 transition-colors">
                  {video.title}
                </h3>
                <p className="font-lato text-gray-500 text-xs mt-1">{video.views} {t('views', 'vistas')}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - Clean, minimal */}
      <section className="py-24 bg-white" data-testid="cta-section">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <div className="w-10 h-px bg-gold-400 mx-auto mb-8" />
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-charcoal mb-4">
            {t("Support Sarah's Music", 'Apoya la Música de Sarah')}
          </h2>
          <p className="font-lato text-base text-gray-500 mb-8 max-w-lg mx-auto">
            {t(
              'Purchase an album and help spread the message of hope through inspirational music.',
              'Compra un álbum y ayuda a difundir el mensaje de esperanza a través de música inspiradora.'
            )}
          </p>
          <Link to="/music" data-testid="cta-shop-now">
            <Button className="bg-gold-500 hover:bg-gold-400 text-white font-lato text-sm uppercase tracking-wider px-10 py-5 rounded-none">
              {t('Shop Now', 'Comprar Ahora')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
